/* eslint-disable max-len */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionButtons,
  DataTable,
  type ActionButtonConfig,
  type DataTableColumn,
  type DataTableExpandedTableConfig,
  type DataTableRow,
} from '@enamelai/design-system'
import { Icon as CallIcon } from '@enamelai/design-system/icons/Call'
import { Icon as NotificationsIcon } from '@enamelai/design-system/icons/Notifications'
import { Icon as ViewIcon } from '@enamelai/design-system/icons/View'
import { ReactComponent as OtpKeyIcon } from '@assets/images/FormBuilder/otp-key.svg'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useFormsTableState } from '@components/pages/FormBuilder/Shared/useFormsTableState'
import CompletedFormsModal from './CompletedFormsModal.component'
import RemindPatientModal from './RemindPatientModal.component'
import PatientOTPModal from './PatientOTPModal.component'
import SelectAppointmentForKioskModal from './SelectAppointmentForKioskModal.component'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { createPatientFormsOtp } from '@services/api/otp.api'
import { useNotificationToast } from '@context/NotificationToastContext'
import { createSuccessToast } from '@utils/notificationToast.util'
import type {
  AppointmentDetail,
  IncompleteFormEntry,
  PendingFormEntry,
} from '@pages/FormBuilder/assignedForms.types'
import type { CompletedFormsViewLocationState } from '@pages/FormBuilder/CompletedFormsView.page'
import './PendingFormsTable.css'
import { setActiveSidePanel } from '@store/reducers/uiSlice.reducer'
import { SET_INPUT_NUMBER } from '@store/reducers/messageCenter.reducer'
import { normalizePhoneNumber } from '@utils/numberFormatter.util'
import type { IContact } from '@dataTypes/contact.type'
import SelectContactModal from '@components/layout/Modals/SelectContact/SelectContactModal.component'

export type { AppointmentDetail, IncompleteFormEntry, PendingFormEntry }

interface PendingFormsTableProps {
  data: PendingFormEntry[]
  onOpenPatientForms?: (patientId: string, patientName?: string) => void
  onOpenCompletedDetail?: (payload: CompletedFormsViewLocationState) => void
  /**
   * When set (e.g. from a "Forms pending before appointment" notification
   * deep-link), the matching patient row is auto-expanded and selected on
   * mount, and again whenever this value changes.
   */
  initialExpandedPatientId?: string | null
}

const REMIND_LOADING_CONTAINER = '.page-content'

/** Set to true to show the Remind button and Last Reminded column in the Pending Forms table. */
const SHOW_REMIND_FEATURE = false

type PendingFormRow = {
  id: string
  patientName: string
  appointmentsCount: number
}

type AppointmentRow = {
  id: string
  request: string
}

const hasPendingForms = (appointment: AppointmentDetail): boolean => {
  const incompleteCount = appointment.incompleteForms?.length ?? 0
  if (incompleteCount > 0) {
    return true
  }

  const totalCount = appointment.totalFormsCount ?? 0
  const completedCount = appointment.completedFormsCount ?? 0
  if (totalCount > 0) {
    return completedCount < totalCount
  }

  return false
}

const getRequestLabel = (appointment: AppointmentDetail): string => {
  if (!appointment.appointmentId) return 'Direct request'
  const label = `${appointment.appointmentType} - ${appointment.roomNo}`
  return appointment.appointmentDate
    ? `${label} (${appointment.appointmentDate})`
    : label
}

const getAppointmentRowId = (
  parent: PendingFormEntry,
  appointment: AppointmentDetail,
  index: number,
): string =>
  `${parent.patientId}:${appointment.appointmentId || 'direct'}:${index}`

const getPatientMobileFromPendingEntry = (
  row: PendingFormEntry,
): string | undefined => {
  const rowWithMobile = row as PendingFormEntry & { patientMobile?: string }
  const rowPatientMobile = rowWithMobile.patientMobile
  if (rowPatientMobile) {
    const normalizedRowMobile = normalizePhoneNumber(rowPatientMobile)
    if (normalizedRowMobile) {
      return normalizedRowMobile
    }
  }
  for (const appointment of row.appointments) {
    const patientMobile = appointment.completedForms?.[0]?.fields
      ?.find((field) => field.section === 'Contact Information')
      ?.items.find((item) => item.label === 'Mobile/Telephone')?.value as
      | string
      | undefined
    const normalizedPatientMobile = patientMobile
      ? normalizePhoneNumber(patientMobile)
      : ''
    if (normalizedPatientMobile) {
      return normalizedPatientMobile
    }
  }
  return undefined
}

const PendingFormsTable: React.FC<PendingFormsTableProps> = ({
  data,
  onOpenPatientForms,
  onOpenCompletedDetail,
  initialExpandedPatientId,
}) => {
  const dispatch = useAppDispatch()
  const contacts = useAppSelector((state) => state.contacts.data)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const [showModal, setShowModal] = useState(false)
  const [selectedPatient, setSelectedPatient] =
    useState<PendingFormEntry | null>(null)
  const [selectedAppointment, setSelectedAppointment] =
    useState<AppointmentDetail | null>(null)
  const [showRemindModal, setShowRemindModal] = useState(false)
  const [remindModalData, setRemindModalData] = useState<{
    patientName: string
    patientMobile?: string
    patientEmail?: string
    appointments: Array<{ id: string; date: string; type: string }>
  } | null>(null)
  const [showSelectAppointmentModal, setShowSelectAppointmentModal] =
    useState(false)
  const [selectAppointmentRow, setSelectAppointmentRow] =
    useState<PendingFormEntry | null>(null)
  const [showOTPModal, setShowOTPModal] = useState(false)
  const [showSelectContactModal, setShowSelectContactModal] = useState(false)
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null)
  const [otpModalData, setOtpModalData] = useState<{
    patientName: string
    patientId: string
    otp: string
    otp_id?: string
    appointmentId?: string
  } | null>(null)

  const [expandedRowIds, setExpandedRowIds] = useState<string[]>(() =>
    initialExpandedPatientId ? [initialExpandedPatientId] : [],
  )
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
    initialExpandedPatientId ?? null,
  )
  // Only re-apply an externally-supplied expansion when it changes to a new id.
  const appliedInitialExpandedPatientIdRef = useRef<string | null>(
    initialExpandedPatientId ?? null,
  )
  useEffect(() => {
    const next = initialExpandedPatientId ?? null
    if (next == null) return
    if (appliedInitialExpandedPatientIdRef.current === next) return
    appliedInitialExpandedPatientIdRef.current = next
    setExpandedRowIds([next])
    setSelectedPatientId(next)
  }, [initialExpandedPatientId])

  const rows = useMemo(
    (): PendingFormRow[] =>
      data.map((entry) => ({
        id: entry.patientId,
        patientName: entry.patientName,
        appointmentsCount: entry.appointments.filter(hasPendingForms).length,
      })),
    [data],
  )
  const entryById = useMemo(
    () => new Map(data.map((entry) => [entry.patientId, entry])),
    [data],
  )
  const getSortValue = useCallback(
    (row: PendingFormRow, columnId: string): string | number =>
      columnId === 'appointmentsCount'
        ? row.appointmentsCount
        : row.patientName,
    [],
  )
  const table = useFormsTableState(rows, getSortValue)

  const columns: DataTableColumn<PendingFormRow>[] = [
    { id: 'patientName', label: 'Patient', sortable: true },
    {
      id: 'appointmentsCount',
      label: 'No. of appointments',
      sortable: true,
      width: 200,
    },
  ]

  const openRemindModal = (row: PendingFormEntry): void => {
    const firstWithForms = row.appointments.find(
      (apt) => apt.completedForms?.length,
    )
    const contactFields = firstWithForms?.completedForms?.[0]?.fields?.find(
      (f) => f.section === 'Contact Information',
    )
    const patientMobile = contactFields?.items.find(
      (item) => item.label === 'Mobile/Telephone',
    )?.value as string | undefined
    const patientEmail = contactFields?.items.find(
      (item) => item.label === 'Email',
    )?.value as string | undefined
    setRemindModalData({
      patientName: row.patientName,
      patientMobile: patientMobile || undefined,
      patientEmail: patientEmail || undefined,
      appointments: row.appointments.map((apt) => ({
        id: apt.appointmentId,
        date: apt.appointmentDate,
        type: apt.appointmentType,
      })),
    })
    setShowRemindModal(true)
  }

  const callPatient = (row: PendingFormEntry): void => {
    const patientContact = contacts.find(
      (contact) => contact.patient_id === row.patientId,
    )
    if (patientContact) {
      setSelectedContact(patientContact)
      setShowSelectContactModal(true)
      return
    }
    const patientMobile = getPatientMobileFromPendingEntry(row)
    if (patientMobile) {
      dispatch(SET_INPUT_NUMBER(patientMobile))
    }
    dispatch(setActiveSidePanel('phone'))
  }

  const renderActions = (row: PendingFormRow): React.ReactElement | null => {
    const entry = entryById.get(row.id)
    if (!entry) return null
    const actions: ActionButtonConfig[] = [
      {
        id: 'call',
        icon: <CallIcon className="ds-action-button__svg" aria-hidden />,
        label: 'Phone',
        tone: 'primary',
        variant: 'solid',
        onClick: () => callPatient(entry),
      },
      {
        id: 'generate-otp',
        icon: <OtpKeyIcon className="ds-action-button__svg" aria-hidden />,
        label: 'Generate OTP',
        tone: 'primary',
        variant: 'outline',
        onClick: () => {
          setSelectAppointmentRow(entry)
          setShowSelectAppointmentModal(true)
        },
      },
      ...(SHOW_REMIND_FEATURE
        ? [
            {
              id: 'remind',
              icon: (
                <NotificationsIcon
                  className="ds-action-button__svg"
                  aria-hidden
                />
              ),
              label: 'Remind patient to complete forms',
              tone: 'primary' as const,
              variant: 'outline' as const,
              onClick: () => openRemindModal(entry),
            },
          ]
        : []),
      {
        id: 'view-forms',
        icon: <ViewIcon className="ds-action-button__svg" aria-hidden />,
        label: 'View patient forms',
        tone: 'primary',
        variant: 'outline',
        onClick: () => {
          if (onOpenPatientForms) {
            showLoading({ container: 'body' })
            onOpenPatientForms(entry.patientId, entry.patientName)
          }
        },
      },
    ]
    return <ActionButtons actions={actions} />
  }

  const appointmentById = useMemo(() => {
    const map = new Map<
      string,
      { parent: PendingFormEntry; appointment: AppointmentDetail }
    >()
    data.forEach((parent) => {
      parent.appointments.forEach((appointment, index) => {
        map.set(getAppointmentRowId(parent, appointment, index), {
          parent,
          appointment,
        })
      })
    })
    return map
  }, [data])

  const renderFormsStatus = (row: DataTableRow): React.ReactNode => {
    const match = appointmentById.get(row.id)
    if (!match) return <span>Completed 0/0</span>
    const { parent, appointment } = match
    const completedCount = appointment.completedFormsCount ?? 0
    const totalCount = appointment.totalFormsCount ?? 0
    if (totalCount === 0) return <span>Completed 0/0</span>

    const contactFields = appointment.completedForms?.[0]?.fields?.find(
      (f) => f.section === 'Contact Information',
    )
    const payload: CompletedFormsViewLocationState = {
      patientName: parent.patientName,
      appointmentType: appointment.appointmentType,
      roomNo: appointment.appointmentId ? appointment.roomNo : '',
      completedForms: appointment.completedForms ?? [],
      incompleteForms: appointment.incompleteForms ?? [],
      patientMobile: contactFields?.items.find(
        (item) => item.label === 'Mobile/Telephone',
      )?.value as string | undefined,
      patientEmail: contactFields?.items.find((item) => item.label === 'Email')
        ?.value as string | undefined,
    }
    return (
      <button
        type="button"
        className="forms-status-link"
        onClick={() => onOpenCompletedDetail?.(payload)}
      >
        Completed {completedCount}/{totalCount}
      </button>
    )
  }

  const expandedTable: DataTableExpandedTableConfig<
    DataTableRow,
    PendingFormRow
  > = {
    columns: [
      { id: 'request', label: 'Request / Appointment - Room no. / Date' },
      {
        id: 'formsStatus',
        label: 'Forms status',
        width: 200,
        renderCell: renderFormsStatus,
      },
    ],
    getRows: (parentRow): AppointmentRow[] => {
      const parent = entryById.get(parentRow.id)
      if (!parent) return []
      return parent.appointments
        .map((appointment, index) => ({ appointment, index }))
        .filter(({ appointment }) => hasPendingForms(appointment))
        .map(({ appointment, index }) => ({
          id: getAppointmentRowId(parent, appointment, index),
          request: getRequestLabel(appointment),
        }))
    },
  }

  if (data.length === 0) {
    return null
  }

  return (
    <div className="pending-forms-table-wrapper">
      <DataTable<PendingFormRow>
        className="pending-forms-table"
        columns={columns}
        rows={table.pageRows}
        selectedRowIds={selectedPatientId ? [selectedPatientId] : []}
        expandable
        expandableColumnId="patientName"
        expandedRowIds={expandedRowIds}
        onExpandedRowIdsChange={setExpandedRowIds}
        expandedTable={expandedTable}
        sortColumnId={table.sortColumnId}
        sortDirection={table.sortDirection}
        onSort={table.onSort}
        pagination={table.pagination}
        showActions
        actionsLabel="Action"
        renderActions={renderActions}
      />
      {showModal && selectedPatient && selectedAppointment && (
        <CompletedFormsModal
          show={showModal}
          onHide={() => {
            setShowModal(false)
            setSelectedPatient(null)
            setSelectedAppointment(null)
          }}
          patientName={selectedPatient.patientName}
          forms={selectedAppointment.completedForms || []}
          patientMobile={
            selectedAppointment.completedForms?.[0]?.fields
              .find((f) => f.section === 'Contact Information')
              ?.items.find((item) => item.label === 'Mobile/Telephone')
              ?.value || undefined
          }
          patientEmail={
            selectedAppointment.completedForms?.[0]?.fields
              .find((f) => f.section === 'Contact Information')
              ?.items.find((item) => item.label === 'Email')?.value || undefined
          }
          appointments={selectedPatient.appointments.map((apt) => ({
            id: apt.appointmentId,
            date: apt.appointmentDate,
            type: apt.appointmentType,
          }))}
        />
      )}
      {showSelectAppointmentModal && selectAppointmentRow && (
        <SelectAppointmentForKioskModal
          show={showSelectAppointmentModal}
          onHide={() => {
            setShowSelectAppointmentModal(false)
            setSelectAppointmentRow(null)
          }}
          patientName={selectAppointmentRow.patientName}
          appointments={selectAppointmentRow.appointments.filter(
            hasPendingForms,
          )}
          onSelect={(appointmentId: string) => {
            const row = selectAppointmentRow
            setShowSelectAppointmentModal(false)
            setSelectAppointmentRow(null)
            createPatientFormsOtp({
              patient_id: row.patientId,
              recipient: 'kiosk',
              ...(appointmentId && { appointment_id: appointmentId }),
            })
              .then((result) => {
                setOtpModalData({
                  patientName: row.patientName,
                  patientId: row.patientId,
                  otp: result.code ?? '',
                  otp_id: result.otp_id,
                  appointmentId,
                })
                setShowOTPModal(true)
              })
              .catch(() => {
                showToast(
                  createSuccessToast(
                    'Error',
                    'Failed to generate OTP. Please try again.',
                  ),
                )
              })
          }}
        />
      )}
      {showRemindModal && remindModalData && (
        <RemindPatientModal
          show={showRemindModal}
          onHide={() => {
            setShowRemindModal(false)
            setRemindModalData(null)
          }}
          patientName={remindModalData.patientName}
          patientMobile={remindModalData.patientMobile}
          patientEmail={remindModalData.patientEmail}
          appointments={remindModalData.appointments}
          onConfirmSend={() => {
            setShowRemindModal(false)
            setRemindModalData(null)
            showLoading({ container: REMIND_LOADING_CONTAINER })
            // Simulate send delay; replace with real API call when available
            setTimeout(() => {
              hideLoading(REMIND_LOADING_CONTAINER)
              showToast(
                createSuccessToast(
                  'Patient Reminder',
                  'Patient has been reminded successfully.',
                ),
              )
            }, 2000)
          }}
        />
      )}
      {showOTPModal && otpModalData && (
        <PatientOTPModal
          show={showOTPModal}
          onHide={() => {
            setShowOTPModal(false)
            setOtpModalData(null)
          }}
          patientName={otpModalData.patientName}
          otp={otpModalData.otp}
          patientId={otpModalData.patientId}
          onRegenerate={() => {
            createPatientFormsOtp({
              patient_id: otpModalData.patientId,
              recipient: 'kiosk',
              ...(otpModalData.appointmentId != null &&
                otpModalData.appointmentId !== '' && {
                  appointment_id: otpModalData.appointmentId,
                }),
            })
              .then((result) => {
                setOtpModalData({
                  ...otpModalData,
                  otp: result.code ?? '',
                  otp_id: result.otp_id,
                })
              })
              .catch(() => {
                showToast(
                  createSuccessToast(
                    'Error',
                    'Failed to regenerate OTP. Please try again.',
                  ),
                )
              })
          }}
        />
      )}
      {showSelectContactModal && selectedContact && (
        <SelectContactModal
          show={showSelectContactModal}
          onHide={() => {
            setShowSelectContactModal(false)
            setSelectedContact(null)
          }}
          contactData={selectedContact}
        />
      )}
    </div>
  )
}

export default PendingFormsTable
